import { IReservationUpcoming } from '@/interfaces/IReservation';
import { IPosTableView } from '@/interfaces/IPosTableView';
import { formatTime, IReservationInstant } from './reservation-time';
import { isLate } from './reservation-status';

export type IReservationHintCandidate = IReservationUpcoming & {
  sessionStatus?: 1 | 2 | 3 | null;
};

/** Display filters cannot hide a party that is physically seated without orders. */
export function getFloorReservationCandidates(
  visible: readonly IReservationHintCandidate[],
  all: readonly IReservationHintCandidate[]
): IReservationHintCandidate[] {
  const candidates = new Map(visible.map((row) => [row.id, row]));
  for (const row of all) {
    if (
      row.status === 'seated' &&
      row.tableSessionId &&
      row.sessionStatus === 1
    )
      candidates.set(row.id, row);
  }
  return [...candidates.values()];
}

/** Open seated sessions win, followed by the next confirmed/arrived booking. */
export function getTableReservations(
  upcoming: readonly IReservationHintCandidate[],
  tableId: number
): IReservationHintCandidate[] {
  return upcoming
    .filter(
      (value) =>
        value.tableIds.includes(tableId) &&
        (value.status === 'confirmed' ||
          value.status === 'arrived' ||
          (value.status === 'seated' &&
            !!value.tableSessionId &&
            (value.sessionStatus === undefined || value.sessionStatus === 1)))
    )
    .sort(
      (a, b) =>
        Number(b.status === 'seated') - Number(a.status === 'seated') ||
        Date.parse(a.startsAt) - Date.parse(b.startsAt) ||
        a.id - b.id
    );
}

export function isPosTableOccupied(
  table: Pick<IPosTableView, 'tableStatus' | 'reservation'>
): boolean {
  return (
    table.tableStatus !== 'empty' || table.reservation?.status === 'seated'
  );
}

export function getPosTableOccupiedSince(
  table: Pick<IPosTableView, 'orders' | 'reservation'>
): number | null {
  const times = table.orders.map((order) =>
    new Date(order.createdAt).getTime()
  );
  if (table.reservation?.status === 'seated' && table.reservation.seatedAt)
    times.push(Date.parse(table.reservation.seatedAt));
  const valid = times.filter(Number.isFinite);
  return valid.length ? Math.min(...valid) : null;
}

export function attachReservationHints(
  views: readonly IPosTableView[] | null | undefined,
  upcoming: readonly IReservationHintCandidate[],
  now: IReservationInstant,
  toleranceMin: number,
  timeZone: string
): IPosTableView[] | null {
  if (!views) return null;
  return views.map((view) => {
    const { reservation: previous, ...table } = view;
    const reservation = getTableReservations(upcoming, view.id)[0];
    if (
      !reservation ||
      (reservation.status !== 'confirmed' &&
        reservation.status !== 'arrived' &&
        reservation.status !== 'seated')
    )
      return table;
    const late = isLate(reservation, now, toleranceMin);
    return {
      ...table,
      reservation: {
        id: reservation.id,
        guestName: reservation.guestName,
        partySize: reservation.partySize,
        startsAt: reservation.startsAt,
        seatedAt: reservation.seatedAt,
        timeLabel: formatTime(
          reservation.status === 'seated' && reservation.seatedAt
            ? reservation.seatedAt
            : reservation.startsAt,
          timeZone
        ),
        status: reservation.status,
        late,
        soon:
          late ||
          new Date(reservation.startsAt).getTime() - new Date(now).getTime() <=
            (toleranceMin + 30) * 60000,
      },
    };
  });
}
